import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { ChatMessage, ChatSession, ChatResponse } from '../types/chat';
import { API_URL } from '../config';

// localStorage : simple cache d'AFFICHAGE de la session active.
// Source de vérité = API backend (cross-device, cross-navigateur) : le cache est montré
// immédiatement puis TOUJOURS remplacé par la version de l'API (lot H3 du 2026-10-01).
// Il était auparavant lu SEUL : une conversation poursuivie sur le téléphone restait
// figée à son ancien état sur l'ordinateur.
const sessionMsgKey = (userId: string, sessionId: string) => `chat_session_${userId}_${sessionId}`;
const TAILLE_PAGE_SESSIONS = 20;

interface MessageApi {
  id?: string | number;
  role: string;
  content: string;
  created_at?: string;
}

interface MessageEnCache {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  sources?: string[];
  dataDate?: string;
}

const lireCache = (cle: string): ChatMessage[] | null => {
  try {
    const brut = localStorage.getItem(cle);
    if (!brut) return null;
    const parsed: MessageEnCache[] = JSON.parse(brut);
    return parsed.map(m => ({ ...m, timestamp: m.timestamp ? new Date(m.timestamp) : new Date() }));
  } catch (e) {
    console.error(`Cache illisible (${cle})`, e);
    return null;
  }
};

const ecrireCache = (cle: string, messages: ChatMessage[]) => {
  try {
    localStorage.setItem(cle, JSON.stringify(messages));
  } catch (e) {
    console.warn('Cache local indisponible', e);
  }
};

const AVERTISSEMENT_NON_ENREGISTRE =
  "Cette conversation n'a pas pu être enregistrée : vous ne la retrouverez pas sur vos autres appareils.";

interface QuotaInfo {
  count: number;
  limit: number;
  remaining: number;
  is_pro: boolean;
}

interface ChatContextType {
  messages: ChatMessage[];
  sessions: ChatSession[];
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;
  errorMeta: { action?: string, url?: string } | null;
  quotaInfo: QuotaInfo | null;
  isPaidUser: boolean;
  avertissement: string | null;
  hasMoreSessions: boolean;
  sendMessage: (text: string) => Promise<void>;
  fetchQuota: () => Promise<void>;
  fetchSessions: () => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  startNewSession: () => void;
  deleteSession: (sessionId: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorMeta, setErrorMeta] = useState<{ action?: string, url?: string } | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [avertissement, setAvertissement] = useState<string | null>(null);
  const [hasMoreSessions, setHasMoreSessions] = useState<boolean>(false);
  // Session réellement demandée : une réponse d'API arrivée après un changement de
  // session ne doit pas écraser l'affichage de la nouvelle.
  const sessionDemandee = useRef<string | null>(null);

  // Rôles réels définis en base
  const PRO_ROLES = ['global_admin', 'admin_platform', 'paid_user', 'paid_user_pro', 'paid_user_experimental'];
  const isPaidUser = PRO_ROLES.includes(user?.role?.name ?? '');

  const fetchQuota = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/chat/quota`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuotaInfo(data);
      }
    } catch (e) {
      console.error("Failed to fetch quota", e);
    }
  }, [token]);

  // Liste triée par DERNIÈRE ACTIVITÉ côté API (lot H4), paginée.
  const pageSessions = useCallback(async (offset: number): Promise<ChatSession[] | null> => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions?limit=${TAILLE_PAGE_SESSIONS}&offset=${offset}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return null;
      const data: ChatSession[] = await res.json();
      return data;
    } catch (e) {
      console.error('Failed to fetch sessions', e);
      return null;
    }
  }, [token]);

  const fetchSessions = useCallback(async () => {
    const data = await pageSessions(0);
    if (data) {
      setSessions(data);
      setHasMoreSessions(data.length === TAILLE_PAGE_SESSIONS);
    }
  }, [pageSessions]);

  const loadMoreSessions = useCallback(async () => {
    const data = await pageSessions(sessions.length);
    if (data) {
      setSessions(prev => [...prev, ...data.filter(s => !prev.some(p => p.id === s.id))]);
      setHasMoreSessions(data.length === TAILLE_PAGE_SESSIONS);
    }
  }, [pageSessions, sessions.length]);

  // Reset complet + chargement des sessions depuis l'API au changement d'utilisateur
  // Garantit l'isolation entre users ET la synchronisation cross-device
  useEffect(() => {
    const userId = user?.id ? String(user.id) : null;

    // Vider le state (sécurité : ne pas laisser fuiter les sessions de l'user précédent)
    setMessages([]);
    setSessions([]);
    setCurrentSessionId(null);
    setError(null);
    setErrorMeta(null);
    setQuotaInfo(null);
    setAvertissement(null);
    setHasMoreSessions(false);
    sessionDemandee.current = null;

    if (!userId || !token) return;

    // Source de vérité = API (cross-device)
    fetchSessions();
    fetchQuota();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Déclenché UNIQUEMENT au changement d'utilisateur

  const startNewSession = useCallback(() => {
    sessionDemandee.current = null;
    setCurrentSessionId(null);
    setMessages([]);
    setError(null);
    setErrorMeta(null);
    setAvertissement(null);
  }, []);

  // Recharge une session depuis l'API (source de vérité) et met le cache à jour.
  const revaliderSession = useCallback(async (sessionId: string) => {
    const userId = user?.id ? String(user.id) : null;
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}/messages?graphiques=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        if (res.status === 404) {
          // Session supprimée ailleurs : on la retire de la liste et du cache.
          setSessions(prev => prev.filter(s => s.id !== sessionId));
          if (userId) localStorage.removeItem(sessionMsgKey(userId, sessionId));
        }
        return;
      }
      const data: MessageApi[] = await res.json();
      // L'API ne conserve ni les sources ni la date de séance : on reprend celles du
      // cache local quand le message est le même (sinon les badges disparaissaient au
      // rechargement de la conversation).
      const cache = userId ? lireCache(sessionMsgKey(userId, sessionId)) : null;
      const depuisApi: ChatMessage[] = data.map((m, i) => {
        const connu = cache?.[i]?.content === m.content ? cache[i] : undefined;
        return {
          id: String(m.id ?? window.crypto.randomUUID()),
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          timestamp: m.created_at ? new Date(m.created_at) : new Date(),
          ...(connu?.sources ? { sources: connu.sources } : {}),
          ...(connu?.dataDate ? { dataDate: connu.dataDate } : {}),
        };
      });
      if (sessionDemandee.current !== sessionId) return;   // l'utilisateur a changé de session
      setMessages(depuisApi);
      if (userId) ecrireCache(sessionMsgKey(userId, sessionId), depuisApi);
    } catch (e) {
      console.error(`Failed to load session ${sessionId} from API`, e);
    }
  }, [user?.id, token]);

  const loadSession = useCallback(async (sessionId: string) => {
    const userId = user?.id ? String(user.id) : null;
    sessionDemandee.current = sessionId;
    setCurrentSessionId(sessionId);
    setAvertissement(null);
    // 1. Affichage immédiat du cache local, s'il existe…
    const cache = userId ? lireCache(sessionMsgKey(userId, sessionId)) : null;
    setMessages(cache ?? []);
    // 2. …puis TOUJOURS la version de l'API, qui fait foi (autre appareil, autre onglet).
    await revaliderSession(sessionId);
  }, [user?.id, revaliderSession]);

  // Retour de l'application au premier plan (onglet, PWA mobile) : la liste et la
  // session ouverte peuvent avoir changé sur un autre appareil.
  useEffect(() => {
    if (!token) return;
    const auPremierPlan = () => {
      if (document.visibilityState !== 'visible') return;
      fetchSessions();
      if (sessionDemandee.current && !isLoading) revaliderSession(sessionDemandee.current);
    };
    document.addEventListener('visibilitychange', auPremierPlan);
    window.addEventListener('focus', auPremierPlan);
    return () => {
      document.removeEventListener('visibilitychange', auPremierPlan);
      window.removeEventListener('focus', auPremierPlan);
    };
  }, [token, fetchSessions, revaliderSession, isLoading]);

  const deleteSession = useCallback(async (sessionId: string) => {
    const userId = user?.id ? String(user.id) : null;
    try {
      if (token) {
        await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (userId) localStorage.removeItem(sessionMsgKey(userId, sessionId));
      if (currentSessionId === sessionId) startNewSession();
    } catch (e) {
      console.error('Failed to delete session', e);
    }
  }, [token, currentSessionId, startNewSession, user?.id]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;
    
    const userMessage: ChatMessage = {
      id: window.crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    const tempBotMessage: ChatMessage = {
      id: 'loading-id',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true
    };

    setMessages(prev => [...prev, userMessage, tempBotMessage]);
    setIsLoading(true);
    setError(null);
    setErrorMeta(null);

    try {
      const history = messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      
      const response = await fetch(`${API_URL}/api/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: text,
          session_id: currentSessionId,
          history: history,
          // Cette interface sait tracer les graphiques ; une ancienne version en cache
          // n'envoie rien et reçoit le texte seul.
          affiche_graphiques: true
        })
      });

      if (response.status === 403) {
        setError("Cette fonctionnalité est réservée aux abonnés Pro.");
        setMessages(prev => prev.filter(m => m.id !== 'loading-id'));
        return;
      }

      if (response.status === 429) {
        const errData = await response.json();
        const detail = errData.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || "Quota quotidien atteint.");
        setError(msg);
        
        if (typeof detail !== 'string' && detail?.action === 'UPGRADE') {
          setErrorMeta({ action: 'UPGRADE', url: detail.url || '/upgrade' });
        }
        setMessages(prev => prev.filter(m => m.id !== 'loading-id'));
        return;
      }

      if (!response.ok) throw new Error("Erreur serveur");

      const data: ChatResponse = await response.json();

      const finalBotMessage: ChatMessage = {
        id: window.crypto.randomUUID(),
        role: 'assistant',
        content: data.reply,
        sources: data.sources,
        timestamp: new Date(),
        isLoading: false,
        showUpgrade: data.show_upgrade,
        dataDate: data.data_date,  // Badge séance EOD
      };

      // Échange hors historique (quota dépassé, refus démo) : session_id nul, rien à
      // enregistrer. L'API renvoyait jusqu'ici un identifiant inventé, ajouté à la
      // liste comme une conversation fantôme (lot H5).
      const sid = data.session_id;
      const enregistre = data.historique_enregistre !== false;
      if (!enregistre && sid) setAvertissement(AVERTISSEMENT_NON_ENREGISTRE);

      setMessages(prev => {
        const newMessages = [...prev.filter(m => m.id !== 'loading-id'), finalBotMessage];
        const userId = user?.id ? String(user.id) : null;
        if (userId && sid && enregistre) ecrireCache(sessionMsgKey(userId, sid), newMessages);
        return newMessages;
      });

      if (sid && enregistre) {
        const maintenant = new Date().toISOString();
        // La conversation active remonte en tête de liste (tri par dernière activité).
        setSessions(prev => {
          const existante = prev.find(s => s.id === sid);
          const title = existante?.title ?? (text.slice(0, 35) + (text.length > 35 ? '…' : ''));
          const session: ChatSession = { id: sid, title, created_at: existante?.created_at ?? maintenant, last_activity_at: maintenant };
          return [session, ...prev.filter(s => s.id !== sid)];
        });
        if (!currentSessionId) {
          sessionDemandee.current = sid;
          setCurrentSessionId(sid);
        }
      }

    } catch (e) {
      console.error("Chat Error:", e);
      setError("Une erreur est survenue, réessayez.");
      setMessages(prev => prev.filter(m => m.id !== 'loading-id'));
    } finally {
      setIsLoading(false);
      await fetchQuota(); // Unique appel couvrant succès et erreur
    }
  }, [messages, currentSessionId, token, fetchQuota, user?.id]);

  return (
    <ChatContext.Provider value={{
      messages,
      sessions,
      currentSessionId,
      isLoading,
      error,
      errorMeta,
      quotaInfo,
      isPaidUser,
      avertissement,
      hasMoreSessions,
      sendMessage,
      fetchQuota,
      fetchSessions,
      loadMoreSessions,
      loadSession,
      startNewSession,
      deleteSession
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
